/**
 * Your crimes (Crimes 2.0) from Torn's API, the way torn.report's Crimes pages sum them up — pure, browser-safe, tested
 * (packages/shared/test/crimes-report.test.ts). Used by the website's "Your crimes" page (website roadmap S09); the
 * extension's crime helpers share the crime list (`CRIME_TYPES`).
 *
 * What Torn's API gives (v2; field names from Torn's OpenAPI spec, as the torn-api crate types them):
 *  - `user/personalstats?cat=crimes` (Public): `personalstats.crimes { offenses {…}, skills { search_for_cash, …, arson },
 *    version }` — every crime's skill in one call ("Crimes Lite": no log needed);
 *  - `user/{crimeId}/crimes` (Minimal): `crimes { nerve_spent, skill, progression_bonus, rewards { money, ammo
 *    { standard, special }, items [{ id, amount }] }, attempts { total, success, fail, critical_fail, subcrimes [{ id,
 *    total, success, fail }] }, uniques [{ id, rewards { items [{ id, amount }], money { min, max } | null, ammo … } }],
 *    miscellaneous {…} }` — one crime's whole record, one call each;
 *  - `torn/crimes` (Public): `crimes [{ id, name, category_id, enhancer_id, enhancer_name, unique_outcomes_count,
 *    unique_outcomes_ids [], notes }]` — names and how many uniques each crime has;
 *  - `torn/{crimeId}/subcrimes` (Public): `subcrimes [{ id, name, nerve_cost }]`;
 *  - the log (Full): crime attempts as log lines (category "Crimes"). Their `data` fields aren't documented, so they are
 *    read leniently (crime name, nerve, money, items gained/lost, jail) and anything unknown is left out, never guessed.
 */

/** Torn's crime types, in its order (the crimes hub's ids; slug = the crimes page's #/route). */
export const CRIME_TYPES = [
  { id: 1, slug: 'searchforcash', name: 'Search for Cash' },
  { id: 2, slug: 'bootlegging', name: 'Bootlegging' },
  { id: 3, slug: 'graffiti', name: 'Graffiti' },
  { id: 4, slug: 'shoplifting', name: 'Shoplifting' },
  { id: 5, slug: 'pickpocketing', name: 'Pickpocketing' },
  { id: 6, slug: 'cardskimming', name: 'Card Skimming' },
  { id: 7, slug: 'burglary', name: 'Burglary' },
  { id: 8, slug: 'hustling', name: 'Hustling' },
  { id: 9, slug: 'disposal', name: 'Disposal' },
  { id: 10, slug: 'cracking', name: 'Cracking' },
  { id: 11, slug: 'forgery', name: 'Forgery' },
  { id: 12, slug: 'scamming', name: 'Scamming' },
  { id: 13, slug: 'arson', name: 'Arson' },
] as const;

export type CrimeType = (typeof CRIME_TYPES)[number];

/** Torn's top crime skill. */
export const MAX_CRIME_SKILL = 100;

export const crimeById = (id: number | null | undefined): CrimeType | null => CRIME_TYPES.find((c) => c.id === id) ?? null;
export const crimeBySlug = (slug: string | null | undefined): CrimeType | null => CRIME_TYPES.find((c) => c.slug === (slug ?? '').toLowerCase()) ?? null;

type Obj = Record<string, unknown>;
const obj = (x: unknown): Obj | null => (x && typeof x === 'object' && !Array.isArray(x) ? (x as Obj) : null);
const list = (x: unknown): unknown[] => (Array.isArray(x) ? x : Object.values(obj(x) ?? {}));
const num = (x: unknown): number | null => {
  const v = typeof x === 'string' && /^-?\d+(\.\d+)?$/.test(x.trim()) ? Number(x) : x;
  return typeof v === 'number' && Number.isFinite(v) ? v : null;
};
const str = (x: unknown): string => (typeof x === 'string' ? x : typeof x === 'number' ? String(x) : '');
/** "Search for Cash" / "search_for_cash" / "searchforcash" → "searchforcash" (names compared loosely). */
export const looseName = (s: string): string => s.toLowerCase().replace(/[^a-z0-9]/g, '');

// ---------------------------------------------------------------- Torn's crime list

export interface CrimeInfo {
  id: number;
  name: string;
  /** How many unique outcomes the crime has, and their ids. */
  uniqueTotal: number | null;
  uniqueIds: number[];
  enhancer: string;
}

/** v2 `torn/crimes` → the crimes (our names when Torn leaves one out). */
export function readCrimeCatalog(body: unknown): CrimeInfo[] {
  const out: CrimeInfo[] = [];
  for (const raw of list(obj(body)?.crimes)) {
    const c = obj(raw);
    const id = num(c?.id);
    if (!c || id === null) continue;
    const ids = list(c.unique_outcomes_ids).map(num).filter((x): x is number => x !== null);
    const total = num(c.unique_outcomes_count);
    out.push({ id, name: str(c.name) || crimeById(id)?.name || `Crime ${id}`, uniqueTotal: total ?? (ids.length || null), uniqueIds: ids, enhancer: str(c.enhancer_name) });
  }
  return out.sort((a, b) => a.id - b.id);
}

export interface SubcrimeInfo {
  id: number;
  name: string;
  nerve: number | null;
}

/** v2 `torn/{crimeId}/subcrimes`. */
export function readSubcrimes(body: unknown): SubcrimeInfo[] {
  const out: SubcrimeInfo[] = [];
  for (const raw of list(obj(body)?.subcrimes)) {
    const s = obj(raw);
    const id = num(s?.id);
    if (s && id !== null) out.push({ id, name: str(s.name) || `#${id}`, nerve: num(s.nerve_cost) });
  }
  return out;
}

// ---------------------------------------------------------------- Crimes Lite: skills from personal stats

export interface CrimesLite {
  /** Skill per crime type id (only the ones Torn gave). */
  skills: Record<number, number>;
  /** Torn's offense counters (total, vandalism, theft…), as it names them. */
  offenses: Record<string, number>;
}

/** v2 `user/personalstats?cat=crimes` (any nesting) → skills per crime and the offense counters. Null if neither is there. */
export function readCrimesLite(body: unknown): CrimesLite | null {
  const crimes = obj(obj(obj(body)?.personalstats)?.crimes) ?? obj(obj(body)?.crimes);
  if (!crimes) return null;
  const skills: Record<number, number> = {};
  const sk = obj(crimes.skills) ?? {};
  for (const [k, v] of Object.entries(sk)) {
    const n = num(v);
    const t = CRIME_TYPES.find((c) => looseName(c.name) === looseName(k));
    if (t && n !== null) skills[t.id] = n;
  }
  const offenses: Record<string, number> = {};
  for (const [k, v] of Object.entries(obj(crimes.offenses) ?? {})) {
    const n = num(v);
    if (n !== null) offenses[k] = n;
  }
  return Object.keys(skills).length || Object.keys(offenses).length ? { skills, offenses } : null;
}

// ---------------------------------------------------------------- one crime's whole record

export interface ItemAmount {
  id: number;
  amount: number;
}

export interface UniqueFound {
  id: number;
  items: ItemAmount[];
  money: { min: number; max: number } | null;
  ammo: string;
}

export interface CrimeStats {
  typeId: number;
  /** When it was read (ms). */
  at: number;
  skill: number | null;
  progressionBonus: number | null;
  nerveSpent: number;
  money: number;
  ammo: { standard: number; special: number };
  items: ItemAmount[];
  attempts: { total: number; success: number; fail: number; critical: number };
  subcrimes: Array<{ id: number; total: number; success: number; fail: number }>;
  uniques: UniqueFound[];
  /** The crime's own counters (Torn's `miscellaneous`), flattened: "dvds_sold" → 120. */
  misc: Record<string, number>;
}

const items = (x: unknown): ItemAmount[] =>
  list(x)
    .map(obj)
    .map((i) => ({ id: num(i?.id) ?? 0, amount: num(i?.amount ?? i?.quantity ?? i?.qty) ?? 1 }))
    .filter((i) => i.id > 0);

function flatNumbers(x: unknown, prefix = '', out: Record<string, number> = {}, depth = 0): Record<string, number> {
  const o = obj(x);
  if (!o || depth > 3) return out;
  for (const [k, v] of Object.entries(o)) {
    const name = prefix ? `${prefix}_${k}` : k;
    const n = num(v);
    if (n !== null) out[name] = n;
    else if (obj(v)) flatNumbers(v, name, out, depth + 1);
    if (Object.keys(out).length >= 60) break;
  }
  return out;
}

/** v2 `user/{crimeId}/crimes` → that crime's record (null when the answer isn't one). */
export function readUserCrime(body: unknown, typeId: number, at: number): CrimeStats | null {
  const c = obj(obj(body)?.crimes);
  if (!c) return null;
  const rw = obj(c.rewards) ?? {};
  const am = obj(rw.ammo) ?? {};
  const at2 = obj(c.attempts) ?? {};
  return {
    typeId,
    at,
    skill: num(c.skill),
    progressionBonus: num(c.progression_bonus),
    nerveSpent: num(c.nerve_spent) ?? 0,
    money: num(rw.money) ?? 0,
    ammo: { standard: num(am.standard) ?? 0, special: num(am.special) ?? 0 },
    items: items(rw.items),
    attempts: { total: num(at2.total) ?? 0, success: num(at2.success) ?? 0, fail: num(at2.fail) ?? 0, critical: num(at2.critical_fail) ?? 0 },
    subcrimes: list(at2.subcrimes)
      .map(obj)
      .map((s) => ({ id: num(s?.id) ?? 0, total: num(s?.total) ?? 0, success: num(s?.success) ?? 0, fail: num(s?.fail) ?? 0 }))
      .filter((s) => s.id > 0),
    uniques: list(c.uniques)
      .map(obj)
      .filter((u): u is Obj => !!u && num(u.id) !== null)
      .map((u) => {
        const r = obj(u.rewards) ?? {};
        const m = obj(r.money);
        const a = obj(r.ammo);
        return {
          id: num(u.id)!,
          items: items(r.items),
          money: m && num(m.min) !== null ? { min: num(m.min)!, max: num(m.max) ?? num(m.min)! } : null,
          ammo: a ? [num(a.amount), str(a.type)].filter((x) => x !== null && x !== '').join(' ') : '',
        };
      }),
    misc: flatNumbers(c.miscellaneous),
  };
}

/** Item ids → market value (null/missing: unknown, counted as 0). */
export type ItemValues = ReadonlyMap<number, number | null>;

export const itemsValue = (list: readonly ItemAmount[], values: ItemValues): number => list.reduce((a, i) => a + i.amount * (values.get(i.id) ?? 0), 0);

export interface CrimeRow {
  type: CrimeType;
  skill: number | null;
  /** Read in full (user/{id}/crimes) — else only the skill from personal stats is known. */
  stats: CrimeStats | null;
  nerve: number | null;
  attempts: number | null;
  /** 0–1 (null: no attempts). */
  successRate: number | null;
  critical: number | null;
  money: number | null;
  itemsValue: number | null;
  /** Money + items' market value. */
  gained: number | null;
  /** Gained per nerve spent. */
  perNerve: number | null;
  uniques: { found: number | null; total: number | null };
}

/** Every crime's line for the overview: full records where read, else the skill from Crimes Lite. */
export function crimeRows(stats: Readonly<Record<number, CrimeStats>>, lite: CrimesLite | null, catalog: readonly CrimeInfo[], values: ItemValues): CrimeRow[] {
  return CRIME_TYPES.map((type) => {
    const s = stats[type.id] ?? null;
    const info = catalog.find((c) => c.id === type.id);
    const iv = s ? itemsValue(s.items, values) : null;
    const gained = s ? s.money + (iv ?? 0) : null;
    return {
      type,
      skill: lite?.skills[type.id] ?? s?.skill ?? null,
      stats: s,
      nerve: s?.nerveSpent ?? null,
      attempts: s?.attempts.total ?? null,
      successRate: s && s.attempts.total ? s.attempts.success / s.attempts.total : null,
      critical: s?.attempts.critical ?? null,
      money: s?.money ?? null,
      itemsValue: iv,
      gained,
      perNerve: s && s.nerveSpent > 0 && gained !== null ? gained / s.nerveSpent : null,
      uniques: { found: s ? s.uniques.length : null, total: info?.uniqueTotal ?? null },
    };
  });
}

/** Sums over the rows read in full (and the skill-100 count over all). */
export function crimeTotals(rows: readonly CrimeRow[]) {
  const full = rows.filter((r) => r.stats);
  const sum = (f: (r: CrimeRow) => number | null) => full.reduce((a, r) => a + (f(r) ?? 0), 0);
  const nerve = sum((r) => r.nerve);
  const gained = sum((r) => r.gained);
  const withTotals = full.filter((r) => r.uniques.total !== null);
  return {
    read: full.length,
    nerve,
    attempts: sum((r) => r.attempts),
    money: sum((r) => r.money),
    itemsValue: sum((r) => r.itemsValue),
    gained,
    perNerve: nerve > 0 ? gained / nerve : null,
    uniquesFound: sum((r) => r.uniques.found),
    uniquesTotal: withTotals.length ? withTotals.reduce((a, r) => a + (r.uniques.total ?? 0), 0) : null,
    maxed: rows.filter((r) => (r.skill ?? 0) >= MAX_CRIME_SKILL).length,
  };
}

/** Unique outcome ids of a crime you haven't found yet (Torn gives ids only — never names). */
export function uniquesMissing(stats: CrimeStats | null, info: CrimeInfo | null): number[] {
  if (!info?.uniqueIds.length) return [];
  const have = new Set((stats?.uniques ?? []).map((u) => u.id));
  return info.uniqueIds.filter((id) => !have.has(id));
}

// ---------------------------------------------------------------- the log: crime attempts

/** A log line as the website reads it (app/read.ts LogEntry). */
export interface LogLike {
  id: string;
  type?: number;
  /** ms */
  at: number;
  title: string;
  category: string;
  data: Record<string, unknown>;
}

export type CrimeOutcome = 'success' | 'fail' | 'critical' | 'other';

export interface CrimeLogEntry {
  id: string;
  at: number;
  /** The crime type when the name says which (null: not known yet). */
  typeId: number | null;
  /** The crime's (or sub-crime's) name as the log gives it. */
  crime: string;
  outcome: CrimeOutcome;
  nerve: number;
  money: number;
  gained: ItemAmount[];
  lost: ItemAmount[];
  /** Money the attempt cost (fines, fees), when the log says. */
  moneyCost: number;
  jailed: boolean;
  unique: boolean;
  title: string;
}

/** Is this a Crimes 2.0 attempt line? (Not organized crimes.) */
export function isCrimeLog(l: LogLike): boolean {
  if (/organi[sz]ed|^oc\b/i.test(`${l.category} ${l.title}`)) return false;
  return /^crimes?$/i.test(l.category.trim()) || /^crime\b/i.test(l.title.trim());
}

/** Items in a log's data field: { "206": 2 }, [{ id, qty }], [206, 207] or a single id. */
export function logItems(x: unknown): ItemAmount[] {
  if (x === null || x === undefined) return [];
  const one = num(x);
  if (one !== null) return one > 0 ? [{ id: one, amount: 1 }] : [];
  if (Array.isArray(x)) {
    return x
      .map((v) => {
        const n = num(v);
        if (n !== null) return { id: n, amount: 1 };
        const o = obj(v);
        return { id: num(o?.id ?? o?.item ?? o?.item_id) ?? 0, amount: num(o?.qty ?? o?.quantity ?? o?.amount) ?? 1 };
      })
      .filter((i) => i.id > 0);
  }
  const o = obj(x);
  if (!o) return [];
  if (num(o.id ?? o.item ?? o.item_id) !== null) return logItems([o]);
  return Object.entries(o)
    .map(([k, v]) => ({ id: num(k) ?? 0, amount: num(v) ?? num(obj(v)?.qty ?? obj(v)?.quantity ?? obj(v)?.amount) ?? 1 }))
    .filter((i) => i.id > 0);
}

const firstOf = (d: Obj, keys: readonly string[]): unknown => {
  for (const k of keys) if (d[k] !== undefined && d[k] !== null && d[k] !== '') return d[k];
  return undefined;
};

/**
 * Crime names (crime types and every sub-crime name known) → crime type id, compared loosely. Sub-crime names come from
 * `torn/{id}/subcrimes` as they're read.
 */
export function crimeNameIndex(subcrimes: Readonly<Record<number, readonly SubcrimeInfo[]>> = {}, catalog: readonly CrimeInfo[] = []): Map<string, number> {
  const m = new Map<string, number>();
  for (const [t, subs] of Object.entries(subcrimes)) for (const s of subs) m.set(looseName(s.name), Number(t));
  for (const c of catalog) m.set(looseName(c.name), c.id);
  for (const c of CRIME_TYPES) {
    m.set(looseName(c.name), c.id);
    m.set(c.slug, c.id);
  }
  return m;
}

/** A crime log line → what it says (null when it isn't one). */
export function readCrimeLogEntry(l: LogLike, names: ReadonlyMap<string, number>): CrimeLogEntry | null {
  if (!isCrimeLog(l)) return null;
  const d = l.data ?? {};
  const crimeRaw = firstOf(d, ['crime', 'crime_name', 'crime_type', 'subcrime', 'subcrime_name', 'crime_action', 'action']);
  const crime = typeof crimeRaw === 'string' ? crimeRaw.replace(/<[^>]*>/g, '').trim() : '';
  const typeRaw = num(firstOf(d, ['crime_type_id', 'crime_id', 'type_id']));
  let typeId = crime ? (names.get(looseName(crime)) ?? null) : null;
  if (typeId === null && typeRaw !== null && crimeById(typeRaw)) typeId = typeRaw;
  const text = `${l.title} ${str(firstOf(d, ['outcome', 'result']))}`.toLowerCase();
  const outcome: CrimeOutcome = /critical/.test(text) ? 'critical' : /fail/.test(text) ? 'fail' : /succe/.test(text) ? 'success' : 'other';
  const jail = firstOf(d, ['jail', 'jailed', 'jail_time', 'jail_duration']);
  return {
    id: l.id,
    at: l.at,
    typeId,
    crime: crime || crimeById(typeId)?.name || '',
    outcome,
    nerve: num(firstOf(d, ['nerve', 'nerve_used', 'nerve_spent', 'nerve_cost'])) ?? 0,
    money: Math.max(0, num(firstOf(d, ['money_gained', 'money', 'cash_gained', 'cash'])) ?? 0),
    gained: logItems(firstOf(d, ['items_gained', 'item_gained', 'items_found', 'item_found', 'items_received'])),
    lost: logItems(firstOf(d, ['items_lost', 'item_lost', 'items_used', 'item_used', 'items_consumed'])),
    moneyCost: Math.max(0, num(firstOf(d, ['money_lost', 'cost', 'fine', 'money_spent'])) ?? 0),
    jailed: /jail/.test(l.title.toLowerCase()) || (jail !== undefined && jail !== false && jail !== 0 && jail !== '0'),
    unique: /unique/.test(l.title.toLowerCase()) || d.unique === true || num(d.unique) === 1 || num(d.unique_id) !== null,
    title: l.title,
  };
}

export interface CrimeLogSum {
  /** Crime type id, or the log's own name when the type isn't known. */
  key: string;
  name: string;
  typeId: number | null;
  attempts: number;
  success: number;
  fail: number;
  critical: number;
  jailed: number;
  uniques: number;
  nerve: number;
  money: number;
  itemsValue: number;
  costs: number;
  profit: number;
  perNerve: number | null;
}

/** Sums per crime (and the whole, key "all") over log entries: torn.report's crime table, profit after costs. */
export function summariseCrimeLog(entries: readonly CrimeLogEntry[], values: ItemValues): { rows: CrimeLogSum[]; all: CrimeLogSum } {
  const blank = (key: string, name: string, typeId: number | null): CrimeLogSum => ({ key, name, typeId, attempts: 0, success: 0, fail: 0, critical: 0, jailed: 0, uniques: 0, nerve: 0, money: 0, itemsValue: 0, costs: 0, profit: 0, perNerve: null });
  const by = new Map<string, CrimeLogSum>();
  const all = blank('all', 'All crimes', null);
  for (const e of entries) {
    const key = e.typeId !== null ? String(e.typeId) : `name:${looseName(e.crime) || 'unknown'}`;
    const row = by.get(key) ?? blank(key, e.typeId !== null ? (crimeById(e.typeId)?.name ?? e.crime) : e.crime || 'Not named in the log', e.typeId);
    by.set(key, row);
    for (const r of [row, all]) {
      r.attempts++;
      if (e.outcome === 'success') r.success++;
      else if (e.outcome === 'fail') r.fail++;
      else if (e.outcome === 'critical') r.critical++;
      if (e.jailed) r.jailed++;
      if (e.unique) r.uniques++;
      r.nerve += e.nerve;
      r.money += e.money;
      r.itemsValue += itemsValue(e.gained, values);
      r.costs += itemsValue(e.lost, values) + e.moneyCost;
    }
  }
  const finish = (r: CrimeLogSum) => {
    r.profit = r.money + r.itemsValue - r.costs;
    r.perNerve = r.nerve > 0 ? r.profit / r.nerve : null;
    return r;
  };
  const rows = [...by.values()].map(finish).sort((a, b) => (a.typeId ?? 99) - (b.typeId ?? 99) || a.name.localeCompare(b.name));
  return { rows, all: finish(all) };
}

/** The value a log chart can show per day. */
export const CRIME_MEASURES = {
  attempts: { label: 'Attempts', of: () => 1 },
  nerve: { label: 'Nerve', of: (e: CrimeLogEntry) => e.nerve },
  money: { label: 'Money', of: (e: CrimeLogEntry) => e.money },
  profit: { label: 'Profit', of: (e: CrimeLogEntry, v: ItemValues) => e.money + itemsValue(e.gained, v) - itemsValue(e.lost, v) - e.moneyCost },
  success: { label: 'Successes', of: (e: CrimeLogEntry) => (e.outcome === 'success' ? 1 : 0) },
  fails: { label: 'Fails (all)', of: (e: CrimeLogEntry) => (e.outcome === 'fail' || e.outcome === 'critical' ? 1 : 0) },
} as const;

export type CrimeMeasure = keyof typeof CRIME_MEASURES;

/** CSV of crime log entries (UTC times; item lists as "id×n"). */
export function crimeLogCsvRows(entries: readonly CrimeLogEntry[]): { header: string[]; rows: Array<Array<string | number>> } {
  const its = (l: ItemAmount[]) => l.map((i) => `${i.id}x${i.amount}`).join(' ');
  return {
    header: ['time_utc', 'crime', 'outcome', 'nerve', 'money', 'items_gained', 'items_lost', 'money_cost', 'jailed', 'unique', 'log_title'],
    rows: entries.map((e) => [new Date(e.at).toISOString(), e.crime, e.outcome, e.nerve, e.money, its(e.gained), its(e.lost), e.moneyCost, e.jailed ? 1 : 0, e.unique ? 1 : 0, e.title]),
  };
}
